import type { BookingWearer } from '../api/bookings';

/**
 * Where a wearer's fitting has got to.
 *
 * <p>
 * <strong>Three states, and the first one is why this is a module rather than a ternary.</strong>
 * `PartyTable` used to render the strip on `appointmentOn && !completedOn` alone, so a wearer with
 * no appointment at all and a wearer already fitted looked identical — nothing. The first is work
 * outstanding that nobody has booked and the second is work finished, and a screen that draws them
 * the same way cannot be used to get a wedding party measured.
 * </p>
 */
export type FittingState = 'unscheduled' | 'scheduled' | 'done';

export function fittingState(wearer: BookingWearer): FittingState {
  if (wearer.fittingCompletedOn !== null) {
    return 'done';
  }

  return wearer.fittingAppointmentOn === null ? 'unscheduled' : 'scheduled';
}

/**
 * Whether the shop owes this person a fitting it has already promised.
 *
 * <p>
 * <strong>`unscheduled` is deliberately not counted, and the temptation to count it is the
 * point.</strong> Now that a fitting can be completed, "not yet fitted" looks like the natural
 * reading — but plenty of shops never book fittings at all, and for an accessories-only booking or
 * a walk-in there is no work pending. Counting those would print "4 unfitted" against nearly every
 * row of the register, which is noise, and noise on that cell is worse than an under-count because
 * the cell exists to say which bookings need work before pickup day.
 * </p>
 *
 * <p>
 * `scheduled` is a commitment the shop made and has not honoured. That is the one worth chasing.
 * </p>
 */
export function isAwaitingFitting(wearer: BookingWearer): boolean {
  return fittingState(wearer) === 'scheduled';
}

/**
 * What the request should say, given the button pressed.
 *
 * <p>
 * Kept out of the drawer because two of the three are easy to get subtly wrong. Marking a fitting
 * done must carry the appointment through — the endpoint takes `appointmentOn` as a *statement*, so
 * omitting it while completing would quietly cancel the appointment on the record. And clearing is
 * an explicit null rather than an absent key, or the server reads it as "leave it alone" and the
 * cancelled fitting stays on the books.
 * </p>
 */
export function fittingRequest(
  intent: 'schedule' | 'complete' | 'clear',
  fields: {
    appointmentOn: string | null;
    completedOn: string | null;
    byEmployeeId: number | null;
  },
): {
  appointmentOn: string | null;
  completed: boolean;
  completedOn: string | null;
  byEmployeeId: number | null;
} {
  if (intent === 'clear') {
    return { appointmentOn: null, completed: false, completedOn: null, byEmployeeId: null };
  }

  return {
    appointmentOn: fields.appointmentOn,
    completed: intent === 'complete',
    completedOn: intent === 'complete' ? fields.completedOn : null,
    byEmployeeId: fields.byEmployeeId,
  };
}

/**
 * What is wrong with what has been typed, in the words the clerk should read.
 *
 * Named `problems` and returning a list, in the shape `draft.ts`, `intake.ts` and `alterations.ts`
 * established: the screen says it before the server has to.
 */
export function fittingProblems(
  intent: 'schedule' | 'complete' | 'clear',
  fields: { appointmentOn: string | null },
): string[] {
  if (intent === 'schedule' && !fields.appointmentOn) {
    return ['Give a date and time for the fitting, or cancel it instead.'];
  }

  return [];
}
